import React, { useState, useEffect, useCallback } from "react";
import cssClasses from "./ViewAllotedCrmDetailsModal.module.css";
import AllotmentFeedbackTab from "./AllotmentFeedbackTab";
import Crm2StepTimeline from "../../../Crm2/Tracker/Crm2StepTimeline";
import { getCrm2Tracker, isCrm2Booking } from "../../../../Services/Crm2Tracker";
import { getSentFeedback } from "../../../../Services/Feedback";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../../Subcomponents/Button";
import ViewPayloadAndErrorDetailsModal from "../ViewCrmDetailsModal/ViewPayloadAndErrorDetailsModal"
import {
  TabContext,
  TabPanel,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  getAllAllotmentTransactions, getApiSuccessLogs, getErrorResponseForApiType
} from "../../../../Services/AllAllotments";
import { errorTost, successTost } from "../../../../Services/Toast";
import { dismissLoader, presentLoader } from "../../../../Services/loader";
import TabsMenu from "../../../../Subcomponents/TabsMenu";
import LeadStatusChip from "../../../../Subcomponents/LeadStatusChip";
import Dot from "../../../Icons/Dot";

function ViewAllotedCrmDetailsModal({
  open,
  handleClose,
  selectedAllotmentObject,
}) {
  const [transactions, setTransactions] = useState([]);
  const [timestamp, setTimeStamp] = useState([]);
  const [viewPayloadAndErrorToggle, setViewPayloadAndErrorToggle] = useState(false);
  const [payload, setPayload] = useState("");
  const [errMsg, setErrMsg] = useState("");
  //API
  const [applicationApiResErr, setApplicationApiResErr] = useState([]);
  const [hundredAmrErr, setHundredAmrErr] = useState([]);
  const [hundredB2bErr, setHundredB2bErr] = useState([]);
  const [gstAmrErr, setGstAmrErr] = useState([]);
  const [gstB2bResErr, setGstB2bResErr] = useState([]);
  const [sdrB2bResErr, setSdrB2bResErr] = useState([]);

  const [tabs, setTabs] = useState([
    {
      text: "Application Details",
      value: "Application Details",
      isSelected: true,
    },
    {
      text: "Transaction Details",
      value: "Transaction Details",
      isSelected: false,
    },
    {
      text: "Lead Details",
      value: "Lead Details",
      isSelected: false,
    },
  ]);

  // ── Customer feedback for this allotment ────────────────────────────────────
  // The feedback service keys its records by source_reference_id, which for the
  // Sales App is the allotmentPaymentId — so this allotment's feedback is
  // fetched by that id server-side rather than pulling the whole list and
  // matching in the browser. The Feedback tab is added only when something comes
  // back, so allotments without feedback look exactly as they did before.
  const [feedbackRecords, setFeedbackRecords] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  const allotmentPaymentIdForFeedback =
    selectedAllotmentObject?.allotmentPaymentId?.allotmentPaymentId;

  const loadFeedback = useCallback(() => {
    if (!allotmentPaymentIdForFeedback) return undefined;

    let cancelled = false;
    setFeedbackLoading(true);
    setFeedbackError(null);

    getSentFeedback({
      source_reference_id: allotmentPaymentIdForFeedback,
      include: "answers",
      page_size: 200,
    })
      .then((res) => {
        if (cancelled) return;
        // One allotment can hold several records — feedback is per stage.
        setFeedbackRecords(res?.results || []);
      })
      .catch(() => {
        if (cancelled) return;
        // Deliberately non-fatal: the rest of the CRM details must still render
        // if the feedback service is unavailable.
        setFeedbackError("Could not load feedback for this allotment.");
        setFeedbackRecords([]);
      })
      .finally(() => {
        if (!cancelled) setFeedbackLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allotmentPaymentIdForFeedback]);

  useEffect(() => loadFeedback(), [loadFeedback]);

  // ── CRM 2.0 (M1-F7, M1.21): Stage 1 Timeline first; Agreement Timeline first after
  // Final Review (prototype crmDetails). Old-flow bookings keep today's tabs.
  const [crm2Tracker, setCrm2Tracker] = useState(null);
  useEffect(() => {
    const id = selectedAllotmentObject?.allotmentPaymentId?.allotmentPaymentId;
    if (!id) return;
    let cancelled = false;
    isCrm2Booking(id)
      .then((isCrm2) => (isCrm2 ? getCrm2Tracker(id) : null))
      .then((tracker) => {
        if (cancelled || !tracker) return;
        setCrm2Tracker(tracker);
        const timelineTabs = tracker.stage === 2 ? ["Agreement Timeline", "Stage 1 Timeline"] : ["Stage 1 Timeline"];
        setTabs((pre) => [
          ...timelineTabs.map((t, i) => ({ text: t, value: t, isSelected: i === 0 })),
          ...pre.filter((t) => !timelineTabs.includes(t.value)).map((t) => ({ ...t, isSelected: false })),
        ]);
      })
      .catch((err) => console.error("[crm2][ViewAllotedCrmDetailsModal] step tracker load failed", { allotmentPaymentId: id, status: err?.response?.status, message: err?.response?.data?.message || err.message }));
    return () => {
      cancelled = true;
    };
  }, [selectedAllotmentObject]);

  // Appended rather than declared up front, so the tab is absent entirely when
  // this allotment has no feedback. Guarded against duplicates.
  useEffect(() => {
    if (feedbackRecords.length === 0) return;
    setTabs((pre) =>
      pre.some((t) => t.value === "Feedback")
        ? pre
        : [...pre, { text: "Feedback", value: "Feedback", isSelected: false }]
    );
  }, [feedbackRecords.length]);

  useEffect(() => {
    // console.log("--selectedAllotmentObject--", selectedAllotmentObject);
    getAllAllotmentTransactions(
      selectedAllotmentObject?.allotmentPaymentId?.allotmentPaymentId
    )
      .then((resp) => {
        setTransactions(resp.data);
      })
      .catch((err) => {
        console.log(err);
      });

    getApiSuccessLogs(
      selectedAllotmentObject?.allotmentPaymentId?.allotmentPaymentId
    ).then((resp) => {
      // console.log("Resp from Success", resp?.data); 
      setTimeStamp(resp?.data);
    }).catch((err) => {
      console.log(err)
    });
  }, [selectedAllotmentObject]);

  useEffect(() => {
    getErrorResponseForApiType(
      selectedAllotmentObject?.allotmentPaymentId?.allotmentPaymentId).then((res) => {
        let data = res?.data;
        // console.log("Errr", data);
        if (data.length !== 0) {
          const uniqueTypes = [...new Set(data.map(obj => obj.type))];
          // console.log("uniqueTypes", uniqueTypes);
          uniqueTypes.forEach((type) => {
            switch (type) {
              case "ApplicationApi":
                if (applicationApiResErr.length === 0)
                  setApplicationApiResErr(filterAndSortErrorRes(data, "ApplicationApi"));
                break;
              case "100AMR":
                if (hundredAmrErr.length === 0)
                  setHundredAmrErr(filterAndSortErrorRes(data, "100AMR"));
                break;
              case "100B2b":
                if (hundredB2bErr.length === 0)
                  setHundredB2bErr(filterAndSortErrorRes(data, "100B2b"));
                break;
              case "gstAMR":
                if (gstAmrErr.length == 0)
                  setGstAmrErr(filterAndSortErrorRes(data, "gstAMR"));
                break;
              case "gstB2bRes":
                if (gstB2bResErr.length == 0)
                  setGstB2bResErr(filterAndSortErrorRes(data, "gstB2bRes"));
                break;
              case "SdrB2bRes":
                if (sdrB2bResErr.length === 0)
                  setSdrB2bResErr(filterAndSortErrorRes(data, "SdrB2bRes"));
                break;
            }
          })
        }
      }).catch((err) => {
        console.log(err);
      })

  }, [applicationApiResErr, hundredAmrErr, hundredB2bErr, gstAmrErr, gstB2bResErr, sdrB2bResErr])


  function filterAndSortErrorRes(data, type) {
    const resArray = data.filter(item => item.type === type);
    const sortedResArray = resArray.sort((a, b) => b.created_at - a.created_at);
    // console.log("sortedResArray", sortedResArray)
    return sortedResArray;
  }

  const getApplicationDetailsTime = (resType, type, time) => {
    let dateInFormat;
    if (resType === "success") {
      const result = timestamp.find(obj => obj["type"] === type);
      dateInFormat = new Date(result?.created_at).toDateString() + " " + new Date(result?.created_at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (resType === "error") {
      dateInFormat = new Date(time).toDateString() + " " + new Date(time).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    // console.log(dateInFormat);
    if (dateInFormat !== "Invalid Date Invalid Date")
      return dateInFormat;
    // else 
    //   return "";
  }

  const getSuccessPayload = (timestamp, type, value) => {
    const currentApi = timestamp.find(obj => obj[type] === value);
    if (currentApi?.payload)
      return JSON.stringify(currentApi?.payload);
    else
      return null;
  }

  async function CreateApplicationToServer() {
    if (!selectedAllotmentObject || !selectedAllotmentObject?.id) {
      errorTost("Something went wrong! Try again");
      handleClose()
      return;
    }

    try {
      presentLoader();
      // const approveTagCrmAllotmentCreateApplicationResponse =
      //   await approveTagCrmAllotmentCreateApplication(
      //     selectedAllotmentObject?.allotmentPaymentId?.allotmentPaymentId
      //   );
      // console.log(
      //   "approveTagCrmAllotmentCreateApplicationResponse => ",
      //   approveTagCrmAllotmentCreateApplicationResponse
      // );
      // if (approveTagCrmAllotmentCreateApplicationResponse?.data?.success) {
      // successTost("Application api hit successfully.");
      // } else {
      //   const _error = {
      //     error: {
      //       message:
      //         approveTagCrmAllotmentCreateApplicationResponse?.data?.message,
      //     },
      //   };
      //   throw _error;
      // }
      successTost("Application api to be called here!")
      handleClose()
      dismissLoader();
    } catch (error) {
      console.log("approveTagCrmAllotmentResponse error => ", error);
      if (error?.error?.message) {
        errorTost(error?.error?.message);
      } else {
        errorTost("Something went wrong! Try again");
      }
      handleClose()
      dismissLoader();
    }
  }

  function numberWithCommas(x) {
    return x?.toString().replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ",");
  }
  return (
    <>
      <Modal
        open={open}
        onClose={() => handleClose({ flag: false })}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={[
          {
            ".MuiBackdrop-root": {
              background: "rgba(193, 201, 210, 0.7)",
            },
          },
        ]}
        disableAutoFocus={true}
      >
        <Box className={cssClasses.modalWrapper}>
          <div className={cssClasses.header}>
            {/* <img src={MessageIcon} alt="" /> */}
            <h1>View CRM Details</h1>
            {/* <img src={crossImage} alt="" style={{cursor:"pointer"}} onClick={handleClose}/> */}
            <IconButton
              className={cssClasses.crossButton}
              onClick={() => handleClose({ flag: false })}
            >
              {" "}
              <CloseIcon />{" "}
            </IconButton>
          </div>

          <div className={cssClasses.mainContainer}>
            <div className={cssClasses.tabsWrapper} style={{ height: "100%" }}>
              <Box style={{ height: "100%" }}>
                <div
                  className={cssClasses.tabsWrapper}
                  style={{ height: "100%" }}
                >
                  <Box
                    className={cssClasses.TabsWrapper}
                    style={{ height: "100%" }}
                  >
                    <TabContext value={tabs.find((f) => f?.isSelected)?.value}>
                      <TabsMenu
                        selectedColor={"#1570EF"}
                        // selectedBackgroundColor={"#FFFFFF"}
                        stylesForTab={{
                          color: "#344054",
                          fontSize: "1.6rem",
                          fontStyle: "normal",
                          fontWeight: "500",
                          lineHeight: "2.4rem",
                          padding: "0.8rem 1.2rem",
                          textTransform: "unset",
                          borderBottom: "1px solid #EAECF0",
                        }}
                        borderColor={"#1570EFs"}
                        stylesForWrapper={{
                          padding: "1.5rem 0",
                          // background: "#FFFFFF",
                        }}
                        list={tabs}
                        value={tabs.find((f) => f?.isSelected)?.value}
                        handleChange={(e, value) => {
                          setTabs((pre) => {
                            pre.forEach((ele) => {
                              if (ele.value === value) {
                                ele.isSelected = true;
                              } else {
                                ele.isSelected = false;
                              }
                            });
                            return [...pre];
                          });
                        }}
                      />
                      {crm2Tracker &&
                        ["Stage 1 Timeline", "Agreement Timeline"].map((t) => (
                          <TabPanel key={t} style={{ height: "calc(100% - 7rem)", overflowY: "auto" }} className={cssClasses.Tabs} value={t}>
                            <Crm2StepTimeline tracker={crm2Tracker} stage={t === "Stage 1 Timeline" ? 1 : 2} heading={selectedAllotmentObject?.lead_name || ""} />
                          </TabPanel>
                        ))}
                      <TabPanel
                        style={{
                          height: "calc(100% - 7rem)",
                          padding: "0rem 0rem 2rem 1rem !important",
                        }}
                        className={cssClasses.Tabs}
                        value={"Application Details"}
                        index={0}
                      >
                        <div>
                          <Timeline position="right" sx={[timelineStyle]}>
                            {/*//todo ---------------> Application Api  */}
                            <TimelineItem>
                              <TimelineSeparator>
                                <TimelineConnector />
                                <div style={{ width: "5rem", height: "5rem" }}>
                                  {selectedAllotmentObject?.allotmentPaymentId
                                    ?.applicationApiRes?.length > 0 ? (
                                    <CheckCircleOutlineIcon
                                      style={{
                                        color: "#32D583",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  ) : (
                                    applicationApiResErr?.length === 0 ? (
                                      <ErrorOutlineIcon
                                        style={{
                                          color: "#FEC84B",
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      />) : (
                                      <HighlightOffIcon
                                        style={{
                                          color: "#D90000",
                                          width: "100%",
                                          height: "100%"
                                        }} />
                                    )
                                  )}
                                </div>
                                {/* </TimelineDot> */}
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent sx={{ py: "12px", px: 2 }}>
                                <Box
                                  display={"flex"}
                                  flexDirection={"row"}
                                  justifyContent={"space-between"}
                                >
                                  <Box>
                                    <Typography variant="h6" component="p">
                                      <p
                                        style={{
                                          fontSize: "1.6rem",
                                          fontFamily: "Inter-Medium",
                                        }}
                                      >
                                        {/* {selectedAllotmentObject
                                        ?.allotmentPaymentId?.applicationApiRes?.length || "Application Done"} */}
                                        {"Application Done"}
                                      </p>
                                    </Typography>
                                    <Typography>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Booking Application No :{" "}
                                        {selectedAllotmentObject
                                          ?.allotmentPaymentId
                                          ?.applicationApiRes &&
                                          selectedAllotmentObject
                                            ?.allotmentPaymentId
                                            ?.applicationApiRes[0]
                                            ?.bookingApplicationNo
                                          ? selectedAllotmentObject
                                            ?.allotmentPaymentId
                                            ?.applicationApiRes[0]
                                            ?.bookingApplicationNo
                                          : "--"}
                                      </p>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Date : - {selectedAllotmentObject?.allotmentPaymentId?.applicationApiRes?.length > 0
                                          ? getApplicationDetailsTime("success", "ApplicationApi", null)
                                          : getApplicationDetailsTime("error", null, applicationApiResErr[0]?.created_at)}
                                      </p>
                                    </Typography>
                                  </Box>
                                  {/* Success Payload */}
                                  {selectedAllotmentObject?.allotmentPaymentId
                                    ?.applicationApiRes?.length > 0 ? (<Box>
                                      <div>
                                        <Button
                                          style={{
                                            color: "#ffffff",
                                            backgroundColor: "#155EEF",
                                          }}
                                          btnName={"View Payload"}
                                          clicked={() => {
                                            setPayload(getSuccessPayload(timestamp, "type", "ApplicationApi") || "Payload not available");
                                            setErrMsg(null);
                                            setViewPayloadAndErrorToggle(true);
                                          }}
                                        />
                                      </div>
                                    </Box>) : (
                                    applicationApiResErr?.length > 0 ? (
                                      <Box sx={{ display: "flex", gap: "1.5rem" }}>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Payload"}
                                            clicked={() => {
                                              setPayload(applicationApiResErr[0]?.payload ? JSON.stringify(applicationApiResErr[0]?.payload) : "Payload not available");
                                              setErrMsg(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Error Logs"}
                                            clicked={() => {
                                              setErrMsg(applicationApiResErr[0]?.errorObj ? JSON.stringify(applicationApiResErr[0]?.errorObj) : "Error not found");
                                              setPayload(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"Retry"}
                                            clicked={() => {
                                              CreateApplicationToServer();
                                            }}
                                          />
                                        </div>
                                      </Box>
                                    ) : ((!selectedAllotmentObject
                                      ?.allotmentPaymentId?.applicationApiRes?.length > 0) &&
                                      <Box>
                                        <div>
                                          {
                                            selectedAllotmentObject?.allotmentPaymentId?.crm_status_id === 5 && <Button
                                              style={{
                                                color: "#ffffff",
                                                backgroundColor: "#155EEF",
                                              }}
                                              btnName={"Retry"}
                                              clicked={() => {
                                                CreateApplicationToServer();
                                              }}
                                            />
                                          }

                                        </div>
                                      </Box>
                                    )
                                  )}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>

                            {/*//todo ---------------> Application Api 100AmrRes*/}
                            <TimelineItem>
                              <TimelineSeparator>
                                <TimelineConnector />
                                <div style={{ width: "5rem", height: "5rem" }}>
                                  {selectedAllotmentObject?.allotmentPaymentId?.[
                                    "100AmrRes"
                                  ] ? (
                                    <CheckCircleOutlineIcon
                                      style={{
                                        color: "#32D583",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  ) : (
                                    hundredAmrErr?.length === 0 ? (
                                      <ErrorOutlineIcon
                                        style={{
                                          color: "#FEC84B",
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      />
                                    ) : (
                                      <HighlightOffIcon
                                        style={{
                                          color: "#D90000",
                                          width: "100%",
                                          height: "100%"
                                        }} />
                                    )
                                  )}
                                </div>
                                {/* </TimelineDot> */}
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent sx={{ py: "12px", px: 2 }}>
                                <Box
                                  display={"flex"}
                                  flexDirection={"row"}
                                  justifyContent={"space-between"}
                                >
                                  <Box>
                                    <Typography variant="h6" component="span">
                                      <p
                                        style={{
                                          fontSize: "1.6rem",
                                          fontFamily: "Inter-Medium",
                                        }}
                                      >
                                        {/* {selectedAllotmentObject
                                    ?.allotmentPaymentId?.["100AmrRes"] || "AMR Basic"} */}
                                        {"AMR Basic"}
                                      </p>
                                    </Typography>
                                    <Typography>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Document No :{" "}
                                        {selectedAllotmentObject
                                          ?.allotmentPaymentId?.["100AmrRes"]?.outputList?.documentNo
                                          ? selectedAllotmentObject
                                            ?.allotmentPaymentId?.["100AmrRes"]?.outputList?.documentNo
                                          : "--"}
                                      </p>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Date : - {selectedAllotmentObject?.allotmentPaymentId?.["100AmrRes"]
                                          ? getApplicationDetailsTime("success", "100AMR", null)
                                          : getApplicationDetailsTime("error", null, hundredAmrErr[0]?.created_at)}
                                      </p>
                                    </Typography>
                                  </Box>
                                  {/* Success Payload */}
                                  {selectedAllotmentObject?.allotmentPaymentId?.["100AmrRes"] ?
                                    (<Box>
                                      <div>
                                        <Button
                                          style={{
                                            color: "#ffffff",
                                            backgroundColor: "#155EEF",
                                          }}
                                          btnName={"View Payload"}
                                          clicked={() => {
                                            setPayload(getSuccessPayload(timestamp, "type", "100AMR") || "Payload not available");
                                            setErrMsg(null);
                                            setViewPayloadAndErrorToggle(true);
                                          }}
                                        />
                                      </div>
                                    </Box>) :
                                    (hundredAmrErr?.length > 0 && (
                                      <Box sx={{ display: "flex", gap: "1.5rem" }}>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Payload"}
                                            clicked={() => {
                                              setPayload(hundredAmrErr[0]?.payload ? JSON.stringify(hundredAmrErr[0]?.payload) : "Payload not available");
                                              setErrMsg(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Error Logs"}
                                            clicked={() => {
                                              setErrMsg(hundredAmrErr[0]?.errorObj ? JSON.stringify(hundredAmrErr[0]?.errorObj) : "Error not found");
                                              setPayload(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                      </Box>
                                    )
                                      //  : (!selectedAllotmentObject?.allotmentPaymentId?.["100AmrRes"] &&
                                      //   <Box>
                                      //     <div>
                                      //       <Button
                                      //         style={{
                                      //           color: "#ffffff",
                                      //           backgroundColor: "#155EEF",
                                      //         }}
                                      //         btnName={"Retry"}
                                      //         clicked={() => {
                                      //           CreateApplicationToServer(); 
                                      //         }}
                                      //         />
                                      //       </div>
                                      //     </Box>
                                      // ))
                                    )}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>

                            {/*//todo ---------------> Application Api 100B2bRes*/}
                            <TimelineItem>
                              <TimelineSeparator>
                                <TimelineConnector />
                                <div style={{ width: "5rem", height: "5rem" }}>
                                  {selectedAllotmentObject?.allotmentPaymentId?.[
                                    "100B2bRes"] ? (
                                    <CheckCircleOutlineIcon
                                      style={{
                                        color: "#32D583",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  ) : (
                                    hundredB2bErr?.length === 0 ? (
                                      <ErrorOutlineIcon
                                        style={{
                                          color: "#FEC84B",
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      />
                                    ) : (
                                      <HighlightOffIcon
                                        style={{
                                          color: "#D90000",
                                          width: "100%",
                                          height: "100%"
                                        }} />
                                    )
                                  )}
                                </div>
                                {/* </TimelineDot> */}
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent sx={{ py: "12px", px: 2 }}>
                                <Box
                                  display={"flex"}
                                  flexDirection={"row"}
                                  justifyContent={"space-between"}
                                >
                                  <Box>
                                    <Typography variant="h6" component="span">
                                      <p
                                        style={{
                                          fontSize: "1.6rem",
                                          fontFamily: "Inter-Medium",
                                        }}
                                      >
                                        {/* {selectedAllotmentObject
                                    ?.allotmentPaymentId?.["100B2bRes"] || "Transferred to Collection Account"
                                   } */}
                                        {"Transferred to Collection Account"}
                                      </p>
                                    </Typography>
                                    <Typography>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Document No :{" "}
                                        {selectedAllotmentObject
                                          ?.allotmentPaymentId?.["100B2bRes"]
                                          ?.outputList?.documentNo
                                          ? selectedAllotmentObject
                                            ?.allotmentPaymentId?.["100B2bRes"]
                                            ?.outputList?.documentNo
                                          : "--"}
                                      </p>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Date : - {selectedAllotmentObject?.allotmentPaymentId?.["100B2bRes"]
                                          ? getApplicationDetailsTime("success", "100B2b", null)
                                          : getApplicationDetailsTime("error", null, hundredB2bErr[0]?.created_at)}
                                      </p>
                                    </Typography>
                                  </Box>
                                  {/* Success Payload */}
                                  {selectedAllotmentObject?.allotmentPaymentId?.["100B2bRes"] ?
                                    (<Box>
                                      <div>
                                        <Button
                                          style={{
                                            color: "#ffffff",
                                            backgroundColor: "#155EEF",
                                          }}
                                          btnName={"View Payload"}
                                          clicked={() => {
                                            setPayload(getSuccessPayload(timestamp, "type", "100B2b") || "Payload not available");
                                            setErrMsg(null);
                                            setViewPayloadAndErrorToggle(true);
                                          }}
                                        />
                                      </div>
                                    </Box>) :
                                    (hundredB2bErr?.length > 0 && (
                                      <Box sx={{ display: "flex", gap: "1.5rem" }}>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Payload"}
                                            clicked={() => {
                                              setPayload(hundredB2bErr[0]?.payload ? JSON.stringify(hundredB2bErr[0]?.payload) : "Payload not available");
                                              setErrMsg(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Error Logs"}
                                            clicked={() => {
                                              setErrMsg(hundredB2bErr[0]?.errorObj ? JSON.stringify(hundredB2bErr[0]?.errorObj) : "Error not found");
                                              setPayload(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                      </Box>
                                    )
                                      // : (!selectedAllotmentObject?.allotmentPaymentId?.["100B2bRes"] &&
                                      //   <Box>
                                      //     <div>
                                      //       <Button
                                      //         style={{
                                      //           color: "#ffffff",
                                      //           backgroundColor: "#155EEF",
                                      //         }}
                                      //         btnName={"Retry"}
                                      //         clicked={() => {
                                      //           CreateApplicationToServer(); 
                                      //         }}
                                      //         />
                                      //       </div>
                                      //     </Box>
                                      // )
                                    )}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>

                            {/*//todo ---------------> Application Api GstAmrRes*/}
                            <TimelineItem>
                              <TimelineSeparator>
                                <TimelineConnector />
                                <div style={{ width: "5rem", height: "5rem" }}>
                                  {selectedAllotmentObject?.allotmentPaymentId
                                    ?.GstAmrRes ? (
                                    <CheckCircleOutlineIcon
                                      style={{
                                        color: "#32D583",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  ) : (
                                    gstAmrErr?.length === 0 ? (
                                      <ErrorOutlineIcon
                                        style={{
                                          color: "#FEC84B",
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      />
                                    ) : (
                                      <HighlightOffIcon
                                        style={{
                                          color: "#D90000",
                                          width: "100%",
                                          height: "100%"
                                        }} />
                                    )
                                  )}
                                </div>
                                {/* </TimelineDot> */}
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent sx={{ py: "12px", px: 2 }}>
                                <Box
                                  display={"flex"}
                                  flexDirection={"row"}
                                  justifyContent={"space-between"}
                                >
                                  <Box>
                                    <Typography variant="h6" component="span">
                                      <p
                                        style={{
                                          fontSize: "1.6rem",
                                          fontFamily: "Inter-Medium",
                                        }}
                                      >
                                        {/* {selectedAllotmentObject?.allotmentPaymentId
                                    ?.GstAmrRes || "AMR GST"} */}
                                        {"AMR GST"}
                                      </p>
                                    </Typography>
                                    <Typography>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Document No :{" "}
                                        {selectedAllotmentObject?.allotmentPaymentId?.GstAmrRes?.outputList?.documentNo ? selectedAllotmentObject?.allotmentPaymentId?.GstAmrRes?.outputList?.documentNo : "--"}
                                      </p>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Date : - {selectedAllotmentObject?.allotmentPaymentId?.GstAmrRes
                                          ? getApplicationDetailsTime("success", "gstAMR", null)
                                          : getApplicationDetailsTime("error", null, gstAmrErr[0]?.created_at)}
                                      </p>
                                    </Typography>
                                  </Box>
                                  {/* success payload */}
                                  {selectedAllotmentObject?.allotmentPaymentId?.GstAmrRes ?
                                    (<Box>
                                      <div>
                                        <Button
                                          style={{
                                            color: "#ffffff",
                                            backgroundColor: "#155EEF",
                                          }}
                                          btnName={"View Payload"}
                                          clicked={() => {
                                            setPayload(getSuccessPayload(timestamp, "type", "gstAMR") || "Payload not available");
                                            setErrMsg(null);
                                            setViewPayloadAndErrorToggle(true);
                                          }}
                                        />
                                      </div>
                                    </Box>) :
                                    (gstAmrErr?.length > 0 && (
                                      <Box sx={{ display: "flex", gap: "1.5rem" }}>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Payload"}
                                            clicked={() => {
                                              setPayload(gstAmrErr[0]?.payload ? JSON.stringify(gstAmrErr[0]?.payload) : "Payload not available");
                                              setErrMsg(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Error Logs"}
                                            clicked={() => {
                                              setErrMsg(gstAmrErr[0]?.errorObj ? JSON.stringify(gstAmrErr[0]?.errorObj) : "Error not found");
                                              setPayload(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                      </Box>
                                    )
                                      // : (!selectedAllotmentObject?.allotmentPaymentId
                                      //   ?.GstAmrRes?.length  &&
                                      //   <Box>
                                      //     <div>
                                      //       <Button
                                      //         style={{
                                      //           color: "#ffffff",
                                      //           backgroundColor: "#155EEF",
                                      //         }}
                                      //         btnName={"Retry"}
                                      //         clicked={() => {
                                      //           CreateApplicationToServer(); 
                                      //         }}
                                      //         />
                                      //       </div>
                                      //     </Box>
                                      // )
                                    )}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>

                            {/*//todo ---------------> Application Api gstB2bRes*/}
                            <TimelineItem>
                              <TimelineSeparator>
                                <TimelineConnector />
                                <div style={{ width: "5rem", height: "5rem" }}>
                                  {selectedAllotmentObject?.allotmentPaymentId
                                    ?.gstB2bRes ? (
                                    <CheckCircleOutlineIcon
                                      style={{
                                        color: "#32D583",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  ) : (
                                    gstB2bResErr?.length === 0 ? (
                                      <ErrorOutlineIcon
                                        style={{
                                          color: "#FEC84B",
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      />
                                    ) : (
                                      <HighlightOffIcon
                                        style={{
                                          color: "#D90000",
                                          width: "100%",
                                          height: "100%"
                                        }} />
                                    )
                                  )}
                                </div>
                                {/* </TimelineDot> */}
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent sx={{ py: "12px", px: 2 }}>
                                <Box
                                  display={"flex"}
                                  flexDirection={"row"}
                                  justifyContent={"space-between"}
                                >
                                  <Box>
                                    <Typography variant="h6" component="span">
                                      <p
                                        style={{
                                          fontSize: "1.6rem",
                                          fontFamily: "Inter-Medium",
                                        }}
                                      >
                                        {/* {selectedAllotmentObject?.allotmentPaymentId
                                    ?.gstB2bRes || "Trasferred to GST Account" } */}
                                        {"Trasferred to GST Account"}
                                      </p>
                                    </Typography>
                                    <Typography>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Document No :{" "}
                                        {selectedAllotmentObject?.allotmentPaymentId
                                          ?.gstB2bRes?.outputList?.documentNo
                                          ? selectedAllotmentObject
                                            ?.allotmentPaymentId?.gstB2bRes
                                            ?.outputList?.documentNo
                                          : "--"}
                                      </p>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Date : - {selectedAllotmentObject?.allotmentPaymentId?.gstB2bRes
                                          ? getApplicationDetailsTime("success", "gstB2b", null)
                                          : getApplicationDetailsTime("error", null, gstB2bResErr[0]?.created_at)}
                                      </p>
                                    </Typography>
                                  </Box>
                                  {/* Success Payload */}
                                  {selectedAllotmentObject?.allotmentPaymentId?.gstB2bRes ?
                                    (<Box>
                                      <div>
                                        <Button
                                          style={{
                                            color: "#ffffff",
                                            backgroundColor: "#155EEF",
                                          }}
                                          btnName={"View Payload"}
                                          clicked={() => {
                                            setPayload(getSuccessPayload(timestamp, "type", "gstB2b") || "Payload not available");
                                            setErrMsg(null);
                                            setViewPayloadAndErrorToggle(true);
                                          }}
                                        />
                                      </div>
                                    </Box>) :
                                    (gstB2bResErr?.length > 0 && (
                                      <Box sx={{ display: "flex", gap: "1.5rem" }}>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Payload"}
                                            clicked={() => {
                                              setPayload(gstB2bResErr[0]?.payload ? JSON.stringify(gstB2bResErr[0]?.payload) : "Payload not available");
                                              setErrMsg(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <Button
                                            style={{
                                              color: "#ffffff",
                                              backgroundColor: "#155EEF",
                                            }}
                                            btnName={"View Error Logs"}
                                            clicked={() => {
                                              setErrMsg(gstB2bResErr[0]?.errorObj ? JSON.stringify(gstB2bResErr[0]?.errorObj) : "Error not found");
                                              setPayload(null);
                                              setViewPayloadAndErrorToggle(true);
                                            }}
                                          />
                                        </div>
                                      </Box>
                                    )
                                      // : (!selectedAllotmentObject?.allotmentPaymentId
                                      //   ?.gstB2bRes?.length &&
                                      //   <Box>
                                      //     <div>
                                      //       <Button
                                      //         style={{
                                      //           color: "#ffffff",
                                      //           backgroundColor: "#155EEF",
                                      //         }}
                                      //         btnName={"Retry"}
                                      //         clicked={() => {
                                      //           CreateApplicationToServer(); 
                                      //         }}
                                      //         />
                                      //       </div>
                                      //     </Box>
                                      // )    
                                    )}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>

                            {/*//todo ---------------> Application Api SdrB2bRes*/}
                            <TimelineItem>
                              <TimelineSeparator>
                                <TimelineConnector />
                                <div style={{ width: "5rem", height: "5rem" }}>
                                  {selectedAllotmentObject?.allotmentPaymentId
                                    ?.SdrB2bRes ? (
                                    <CheckCircleOutlineIcon
                                      style={{
                                        color: "#32D583",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  ) : (
                                    sdrB2bResErr?.length === 0 ? (
                                      <ErrorOutlineIcon
                                        style={{
                                          color: "#FEC84B",
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      />
                                    ) : (
                                      <HighlightOffIcon
                                        style={{
                                          color: "#D90000",
                                          width: "100%",
                                          height: "100%"
                                        }} />
                                    )
                                  )}
                                </div>
                                {/* </TimelineDot> */}
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent sx={{ py: "12px", px: 2 }}>
                                <Box
                                  display={"flex"}
                                  flexDirection={"row"}
                                  justifyContent={"space-between"}
                                >
                                  <Box>
                                    <Typography variant="h6" component="span">
                                      <p
                                        style={{
                                          fontSize: "1.6rem",
                                          fontFamily: "Inter-Medium",
                                        }}
                                      >
                                        {/* {selectedAllotmentObject?.allotmentPaymentId
                                    ?.SdrB2bRes || "Transferred to SDR account" } */}
                                        {"Transferred to SDR account"}
                                      </p>
                                    </Typography>
                                    <Typography>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Document No :{" "}
                                        {selectedAllotmentObject?.allotmentPaymentId
                                          ?.SdrB2bRes?.outputList?.documentNo
                                          ? selectedAllotmentObject
                                            ?.allotmentPaymentId?.SdrB2bRes?.outputList?.documentNo
                                          : "--"}
                                      </p>
                                      <p
                                        style={{
                                          fontSize: "1.4rem",
                                          fontFamily: "Inter-Regular",
                                        }}
                                      >
                                        Date : - {selectedAllotmentObject?.allotmentPaymentId?.SdrB2bRes
                                          ? getApplicationDetailsTime("success", "amrGST", null)
                                          : getApplicationDetailsTime("error", null, sdrB2bResErr[0]?.created_at)}
                                      </p>
                                    </Typography>
                                  </Box>
                                  {/* Success Payload */}
                                  {selectedAllotmentObject?.allotmentPaymentId?.SdrB2bRes ?
                                    (<Box>
                                      <div>
                                        <Button
                                          style={{
                                            color: "#ffffff",
                                            backgroundColor: "#155EEF",
                                          }}
                                          btnName={"View Payload"}
                                          clicked={() => {
                                            setPayload(getSuccessPayload(timestamp, "type", "amrGST") || "Payload not available");
                                            setErrMsg(null);
                                            setViewPayloadAndErrorToggle(true);
                                          }}
                                        />
                                      </div>
                                    </Box>) : (
                                      sdrB2bResErr?.length > 0 && (
                                        <Box sx={{ display: "flex", gap: "1.5rem" }}>
                                          <div>
                                            <Button
                                              style={{
                                                color: "#ffffff",
                                                backgroundColor: "#155EEF",
                                              }}
                                              btnName={"View Payload"}
                                              clicked={() => {
                                                setPayload(sdrB2bResErr[0]?.payload ? JSON.stringify(sdrB2bResErr[0]?.payload) : "Payload not available");
                                                setErrMsg(null);
                                                setViewPayloadAndErrorToggle(true);
                                              }}
                                            />
                                          </div>
                                          <div>
                                            <Button
                                              style={{
                                                color: "#ffffff",
                                                backgroundColor: "#155EEF",
                                              }}
                                              btnName={"View Error Logs"}
                                              clicked={() => {
                                                setErrMsg(sdrB2bResErr[0]?.errorObj ? JSON.stringify(sdrB2bResErr[0]?.errorObj) : "Error not found");
                                                setPayload(null);
                                                setViewPayloadAndErrorToggle(true);
                                              }}
                                            />
                                          </div>
                                        </Box>
                                      )
                                      // : (!selectedAllotmentObject?.allotmentPaymentId
                                      //   ?.SdrB2bRes?.length  &&
                                      //   <Box>
                                      //     <div>
                                      //       <Button
                                      //         style={{
                                      //           color: "#ffffff",
                                      //           backgroundColor: "#155EEF",
                                      //         }}
                                      //         btnName={"Retry"}
                                      //         clicked={() => {
                                      //           CreateApplicationToServer(); 
                                      //         }}
                                      //         />
                                      //       </div>
                                      //     </Box>
                                      // )
                                    )}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>

                            {/*//todo ---------------> Application Api WithoutTdsRes*/}
                            {/* <TimelineItem>
                            <TimelineSeparator>
                              <TimelineConnector />
                              <div style={{ width: "5rem", height: "5rem" }}>
                                {selectedAllotmentObject?.allotmentPaymentId
                                  ?.WithoutTdsRes && (
                                    <CheckCircleOutlineIcon
                                      style={{
                                        color: "#32D583",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  )}
                                {!selectedAllotmentObject?.allotmentPaymentId
                                  ?.WithoutTdsRes && (
                                    <ErrorOutlineIcon
                                      style={{
                                        color: "#FEC84B",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    />
                                  )}
                                { !selectedAllotmentObject?.allotmentPaymentId?.WithoutTdsRes &&
          <HighlightOffIcon style={{ color: "#D90000", width: "100%", height: "100%" }} /> //* Red Cross
        } 
                              </div>
                               <TimelineDot/>
                              <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent sx={{ py: "12px", px: 2 }}>
                              <Typography variant="h6" component="span">
                                <p
                                  style={{
                                    fontSize: "1.6rem",
                                    fontFamily: "Inter-Medium",
                                  }}
                                >
                                  {selectedAllotmentObject?.allotmentPaymentId
                                    ?.WithoutTdsRes
                                    ? "Without TDS Api Completed"
                                    : "Without TDS Api Pending"}
                                </p>
                              </Typography>
                              <Typography>
                                <p
                                  style={{
                                    fontSize: "1.4rem",
                                    fontFamily: "Inter-Regular",
                                  }}
                                >
                                  Document No :{" "}
                                  {selectedAllotmentObject?.allotmentPaymentId
                                    ?.WithoutTdsRes?.documentNo
                                    ? selectedAllotmentObject
                                        ?.allotmentPaymentId?.WithoutTdsRes?.documentNo
                                    : "--"}
                                </p>
                              </Typography>
                               <Typography>
        <p style={{ fontSize: "1.4rem", fontFamily: "Inter-Regular" }}>
          {new Date(m.last_updated_at).toLocaleString()}
        </p>
      </Typography> 
                            </TimelineContent>
                          </TimelineItem> */}
                          </Timeline>
                        </div>
                      </TabPanel>
                      <TabPanel
                        style={{
                          height: "calc(100% - 10rem)",
                          padding: "0rem !important",
                        }}
                        className={cssClasses.Tabs}
                        value={"Transaction Details"}
                        index={1}
                      >
                        <div style={{ display: "flex", gap: "5rem" }}>
                          <div style={{ flex: "50%" }}>
                            {transactions.length > 0 && (
                              <div className={cssClasses.unitDetails}>
                                <div
                                  style={{
                                    height: "64rem",
                                    overflow: "auto",
                                  }}
                                >
                                  <div>
                                    <p style={{ fontSize: "2.4rem" }}>
                                      All Transactions :{" "}
                                    </p>
                                  </div>
                                  <div style={{ marginTop: "2rem" }}>
                                    <Timeline
                                      position="right"
                                      sx={[
                                        {
                                          padding: "0rem !important",
                                          [`& .MuiTimeline-root `]: {
                                            padding: "0rem !important",
                                          },

                                          "& . MuiTypography-root": {
                                            padding: "19px 16px !important",
                                          },
                                          [`& .MuiTimelineItem-root:before`]: {
                                            flex: "0rem !important",
                                            padding: "0rem !important",
                                            "-webkitFlex": "none !important",
                                            //  content:'none '
                                          },
                                        },
                                      ]}
                                    >
                                      {transactions.map((m) => (
                                        <TimelineItem>
                                          <TimelineSeparator>
                                            <TimelineConnector />
                                            {/* <TimelineDot> */}
                                            <div
                                              style={{
                                                width: "5rem",
                                                height: "5rem",
                                              }}
                                            >
                                              {m.status === "complete" && (
                                                <CheckCircleOutlineIcon
                                                  style={{
                                                    color: "#32D583",
                                                    width: "100%",
                                                    height: "100%",
                                                  }}
                                                />
                                              )}
                                              {(m.status === "expired" ||
                                                m.status === "failed") && (
                                                  <HighlightOffIcon
                                                    style={{
                                                      color: "#D90000",
                                                      width: "100%",
                                                      height: "100%",
                                                    }}
                                                  />
                                                )}
                                              {m.status === "pending" && (
                                                <ErrorOutlineIcon
                                                  style={{
                                                    color: "#FEC84B",
                                                    width: "100%",
                                                    height: "100%",
                                                  }}
                                                />
                                              )}
                                            </div>
                                            {/* </TimelineDot> */}
                                            <TimelineConnector />
                                          </TimelineSeparator>
                                          <TimelineContent
                                            sx={{ py: "12px", px: 2 }}
                                          >
                                            <Typography
                                              variant="h6"
                                              component="span"
                                            >
                                              <p
                                                style={{
                                                  fontSize: "1.6rem",
                                                  fontFamily: "Inter-Medium",
                                                }}
                                              >
                                                {m.modeOfPayment}
                                              </p>
                                            </Typography>
                                            <Typography>
                                              <p
                                                style={{
                                                  fontSize: "1.4rem",
                                                  fontFamily: "Inter-Regular",
                                                }}
                                              >
                                                amount :{" "}
                                                {numberWithCommas(m.amount)}
                                              </p>
                                            </Typography>
                                            <Typography>
                                              <p
                                                style={{
                                                  fontSize: "1.4rem",
                                                  fontFamily: "Inter-Regular",
                                                }}
                                              >
                                                {new Date(
                                                  m.last_updated_at
                                                ).toLocaleString()}
                                              </p>
                                            </Typography>
                                          </TimelineContent>
                                        </TimelineItem>
                                      ))}
                                    </Timeline>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ flex: "50%", fontSize: "2rem" }}>
                            <div
                              className={cssClasses.tokenStatus}
                              style={{ marginBottom: "2rem" }}
                            >
                              {!selectedAllotmentObject?.sdrcomplete && (
                                <LeadStatusChip
                                  label={"SDR Pending"}
                                  icon={
                                    <Dot width={"0.5rem"} height={"0.5rem"} />
                                  }
                                  style={{
                                    color: "#B42318",
                                    backgroundColor: "#FEF3F2",
                                  }}
                                />
                              )}
                              {selectedAllotmentObject?.sdrcomplete && (
                                <LeadStatusChip
                                  label={"SDR Complete"}
                                  icon={
                                    <Dot width={"0.5rem"} height={"0.5rem"} />
                                  }
                                  style={{
                                    color: "#027A48",
                                    backgroundColor: "#ECFDF3",
                                  }}
                                />
                              )}
                            </div>
                            {(selectedAllotmentObject?.allotmentPaymentId?.paidAmt
                              ?.sdr || selectedAllotmentObject?.allotmentPaymentId?.paidAmt
                                ?.sdr == 0) && (
                                <>
                                  <p style={{ marginBottom: "1rem" }}>
                                    Paid OCR : ₹
                                    {numberWithCommas(
                                      selectedAllotmentObject?.allotmentPaymentId
                                        ?.paidAmt?.ocr
                                    )}
                                  </p>
                                  <p style={{ marginBottom: "1rem" }}>
                                    Paid GST : ₹
                                    {numberWithCommas(
                                      selectedAllotmentObject?.allotmentPaymentId
                                        ?.paidAmt?.gst
                                    )}
                                  </p>
                                  <p style={{ marginBottom: "1rem" }}>
                                    Total SDR : ₹
                                    {numberWithCommas(
                                      selectedAllotmentObject?.unitcost ? selectedAllotmentObject?.unitcost?.sdr : selectedAllotmentObject?.allotmentPaymentId
                                        ?.unitCost?.sdr
                                    )}
                                  </p>
                                  <p style={{ marginBottom: "1rem" }}>
                                    Paid SDR : ₹
                                    {numberWithCommas(
                                      selectedAllotmentObject?.allotmentPaymentId
                                        ?.paidAmt?.sdr
                                    )}
                                  </p>
                                  <p style={{ marginBottom: "1rem" }}>
                                    Pending SDR : ₹
                                    {numberWithCommas(
                                      ((selectedAllotmentObject?.unitcost ? selectedAllotmentObject?.unitcost?.sdr : selectedAllotmentObject?.allotmentPaymentId
                                        ?.unitCost?.sdr) -
                                        selectedAllotmentObject?.allotmentPaymentId
                                          ?.paidAmt?.sdr) > 0 ? ((selectedAllotmentObject?.unitcost ? selectedAllotmentObject?.unitcost?.sdr : selectedAllotmentObject?.allotmentPaymentId
                                            ?.unitCost?.sdr) -
                                            selectedAllotmentObject?.allotmentPaymentId
                                              ?.paidAmt?.sdr) : 0
                                    )}
                                  </p>
                                </>
                              )}
                          </div>
                        </div>
                      </TabPanel>
                      <TabPanel
                        style={{
                          height: "60vh", //calc(100% - 10rem)
                          padding: "2rem 6rem",
                        }}
                        className={cssClasses.Tabs}
                        value={"Lead Details"}
                        index={2}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                            fontSize: "1.6rem",
                          }}
                        >
                          <div>
                            <span style={{ fontSize: "1.8rem", fontWeight: 600 }}>
                              Lead Name :{" "}
                            </span>
                            {selectedAllotmentObject?.lead_name ?
                              <span style={{ opacity: "0.8" }}>
                                {selectedAllotmentObject?.lead_name}
                              </span>
                              : <span style={{ opacity: "0.8" }}>
                                {selectedAllotmentObject?.allotmentPaymentId?.personId?.name}
                              </span>
                            }
                          </div>
                          <div>
                            <span style={{ fontSize: "1.8rem", fontWeight: 600 }}>
                              Mobile Number :
                            </span>
                            {selectedAllotmentObject?.bookingformcontactnumber ?
                              <span style={{ opacity: "0.8" }}>
                                {" "}
                                {`+91 ${selectedAllotmentObject?.bookingformcontactnumber}`}
                              </span>
                              :
                              <span style={{ opacity: "0.8" }}>
                                {" "}
                                {`+91 ${selectedAllotmentObject?.allotmentPaymentId?.bookingFormId?.contactNumber}`}
                              </span>
                            }
                          </div>

                          <div>
                            <span style={{ fontSize: "1.8rem", fontWeight: 600 }}>
                              Email Address :{" "}
                            </span>
                            {selectedAllotmentObject?.lead_email ?
                              <span style={{ opacity: "0.8" }}>
                                {selectedAllotmentObject?.lead_email}
                              </span>
                              : <span style={{ opacity: "0.8" }}>
                                {selectedAllotmentObject?.allotmentPaymentId?.bookingFormId?.email}
                              </span>
                            }
                          </div>
                          <div>
                            <span style={{ fontSize: "1.8rem", fontWeight: 600 }}>
                              CP Name :{" "}
                            </span>
                            <span style={{ opacity: "0.8" }}>
                              {selectedAllotmentObject?.cpname}
                            </span>
                          </div>

                        </div>
                      </TabPanel>
                      {/* Only mounted once this allotment has feedback — the tab
                          itself is added conditionally above. */}
                      {feedbackRecords.length > 0 && (
                        <TabPanel
                          style={{
                            height: "60vh",
                            // Narrower gutter than the other tabs: the cards are the
                            // content here, so they get the width.
                            padding: "2rem 2.4rem",
                            overflowY: "auto",
                          }}
                          className={cssClasses.Tabs}
                          value={"Feedback"}
                          index={3}
                        >
                          <AllotmentFeedbackTab
                            records={feedbackRecords}
                            loading={feedbackLoading}
                            error={feedbackError}
                            onResent={loadFeedback}
                          />
                        </TabPanel>
                      )}
                    </TabContext>
                  </Box>
                </div>
                {/* {mainContent()} */}
              </Box>
            </div>
          </div>

          <div className={cssClasses.footer}>
            {/* <div><Button style={{ color: "#7F56D9", backgroundColor: "#ffffff" }} btnName={"Download PDF"} clicked={handleClose} /></div> */}
            <div>
              <Button
                style={{ color: "#344054", backgroundColor: "#ffffff" }}
                btnName={"Close"}
                clicked={() => handleClose({ flag: false })}
              />
            </div>
            {/* <div><Button style={{ color: "#ffffff", backgroundColor: "#155EEF" }} btnName={"Send Mail TO Lead"} clicked={() => {submitHandler()}}/></div> */}
          </div>
        </Box>
      </Modal>
      {viewPayloadAndErrorToggle && <ViewPayloadAndErrorDetailsModal
        open={Boolean(viewPayloadAndErrorToggle)}
        handleClose={() => {
          setViewPayloadAndErrorToggle(false);
        }}
        heading={payload ? "View Payload" : "View Error Logs"}
        displayMsg={payload ? payload : errMsg}
        copyFlag={payload ? true : false}
      />}
    </>
  );
}

export default ViewAllotedCrmDetailsModal;
const labelStyle = {
  fontWeight: "500",
  fontSize: "14px",
  lineHeight: "20px",
  color: "#344054",
  marginBottom: "0.8rem",
};
const timelineStyle = {
  padding: "0rem !important",
  [`& .MuiTimeline-root `]: {
    padding: "0rem !important",
  },

  "& . MuiTypography-root": {
    padding: "19px 16px !important",
  },
  [`& .MuiTimelineItem-root:before`]: {
    flex: "0rem !important",
    padding: "0rem !important",
    "-webkitFlex": "none !important",
    //  content:'none '
  },
};
